<template>
  <div
    role="search"
    aria-label="Suche"
    class="absolute"
    style="width: clamp(300px, 500px, calc(100vw - (3 / 24 * 100%)))"
  >
    <div class="bg-white shadow" :class="[{ 'px-4 py-2': !displayFancy }]">
      <div class="flex items-baseline justify-between gap-2 px-4 py-3">
        <input
          id="quick-search-input"
          name="q"
          type="text"
          ref="input-field"
          v-model="query"
          placeholder="Wir finden, was Sie suchen."
          aria-label="Suchbegriff"
          @input="handleInput"
          @focus="focused = true"
          @blur="focused = false"
          :class="['grow border-none bg-transparent p-0 focus:ring-0']"
        />
        <i
          v-if="loading"
          class="fa-regular fa-spinner fa-spin text-darkgray"
          aria-hidden="true"
        ></i>
        <i v-else class="fa-regular fa-search" aria-hidden="true"></i>
        <span class="sr-only" aria-live="polite">{{ liveMessage }}</span>
      </div>

      <div
        v-if="searchError && !loading"
        class="px-4 pb-3 text-sm text-darkgray"
      >
        Suche ist aktuell nicht verfügbar.
      </div>

      <div
        class="grid overflow-hidden transition-[grid-template-rows] duration-500"
        :class="
          showResults && hits.length > 0 ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        "
      >
        <TransitionGroup
          tag="div"
          class="flex min-h-0 flex-col"
          enter-active-class="transition-[grid-template-rows,opacity] duration-300 ease-out"
          enter-from-class="grid-rows-[0fr] opacity-0"
          enter-to-class="grid-rows-[1fr] opacity-100"
          leave-active-class="transition-[grid-template-rows,opacity] duration-300 ease-out"
          leave-from-class="grid-rows-[1fr] opacity-100"
          leave-to-class="grid-rows-[0fr] opacity-0"
        >
          <div
            v-for="(item, index) in displayItems"
            :key="item.objectID"
            class="grid"
          >
            <div class="overflow-hidden">
              <component
                :is="item.displayType === 'hit' ? 'a' : 'div'"
                :href="item.displayType === 'hit' ? item.url : undefined"
                @click="handleClick($event, item, index)"
                :class="[
                  'block w-full px-4 py-2 text-base',
                  item.displayType === 'hit'
                    ? displayFancy
                      ? 'hover:bg-lightgray focus:bg-lightgray'
                      : 'hover:bg-lightgray focus:bg-lightgray'
                    : '',
                  activeHit === index ? 'bg-lightgray' : '',
                ]"
              >
                <span class="flex items-baseline justify-between gap-2">
                  <span
                    class="result-item line-clamp-1"
                    v-html="titleHtml(item)"
                  ></span>
                  <span
                    v-if="item.displayType === 'hit' && item.type"
                    class="shrink-0 text-sm text-darkgray"
                    >{{ item.type }}</span
                  >
                </span>
              </component>
            </div>
          </div>
          <div class="grid" key="show-all-results">
            <div class="overflow-hidden">
              <div v-if="hits?.length" class="flex">
                <a
                  :href="searchParamLink"
                  class="size-full px-4 py-2 text-base transition-colors focus:bg-lightgray"
                  :class="[
                    activeHit === displayItems.length ? 'bg-lightgray' : '',
                    displayFancy ? 'hover:bg-lightgray' : 'hover:bg-lightgray',
                  ]"
                >
                  <span>Alle Resultate ({{ nbHits }})</span>
                </a>
              </div>
            </div>
          </div>
        </TransitionGroup>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.result-item em {
  @apply bg-primary px-1 not-italic text-white;
}
</style>

<script setup lang="ts">
import { onMounted, ref, computed, useTemplateRef, onUnmounted } from 'vue';
import {
  searchClient,
  indexName,
  BASE_SEARCH_PARAMS,
  setupInsights,
  trackClick,
  AlgoliaHit,
} from '../utils/algolia';
import { pushDataLayer } from '../utils/tracking';

const { displayFancy } = defineProps<{
  displayFancy: boolean;
}>();

const query = ref('');
const inputField = useTemplateRef('input-field');
const loading = ref(false);
const searchError = ref(false);
const focused = ref(false);
const hits = ref<AlgoliaHit[]>([]);
const lastHits = ref<AlgoliaHit[]>([]);
const nbHits = ref(0);
const activeHit = ref<number>(-1);
const showResults = ref(true);
const queryId = ref('');

const liveMessage = computed(() => {
  if (!query.value.trim() || loading.value) return '';
  if (hits.value.length === 0) return 'Keine Resultate';
  return `${nbHits.value} Resultate`;
});

// `title` is always a scalar highlight leaf; guard the nested-array arm the
// `AlgoliaSnippetNode` union allows for array attributes, and fall back to plain text.
const titleHtml = (item: AlgoliaHit): string => {
  const node = item._highlightResult?.title;
  return node && !Array.isArray(node) ? node.value : item.title;
};

onMounted(() => {
  focusInputField();
  setupInsights();
  window.addEventListener('keydown', keyboardListener);
});

onUnmounted(() => {
  window.removeEventListener('keydown', keyboardListener);
});

const focusInputField = () => {
  inputField.value?.focus();
};

const search = async () => {
  lastHits.value = hits.value.slice(0, 3);
  showResults.value = true;
  searchError.value = false;
  if (!query.value.trim()) {
    hits.value = [];
    return;
  }

  try {
    const response = await searchClient.searchSingleIndex<AlgoliaHit>({
      indexName,
      searchParams: {
        ...BASE_SEARCH_PARAMS,
        query: query.value,
        hitsPerPage: 3,
        clickAnalytics: true,
      },
    });
    hits.value = response.hits as AlgoliaHit[];
    nbHits.value = response.nbHits ?? 0;
    queryId.value = response.queryID ?? '';
    activeHit.value = -1;
  } catch (error) {
    console.error('Algolia search failed:', error);
    searchError.value = true;
    hits.value = [];
  }
};

const keyboardListener = (event: KeyboardEvent) => {
  // Only handle keys while the search field is focused, so arrow keys don't
  // hijack page scrolling elsewhere on the page.
  if (!focused.value) return;

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    navigateResults('down');
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    navigateResults('up');
  } else if (event.key === 'Enter') {
    handleEnter();
  } else if (event.key === 'Escape') {
    if (showResults.value) showResults.value = false;
    else query.value = '';
  }
};

// Report a quick-search to Matomo only when the user commits to a result
// (Enter on a highlighted hit, or a click). The "all results" fall-through
// navigates to /search, where Search.vue records the Site Search instead, so
// we avoid both keystroke noise and double-counting.
const trackQuickSearch = () => {
  const trimmed = query.value.trim();
  if (!trimmed) return;
  pushDataLayer({
    event: 'site_search',
    search_keyword: trimmed,
    search_count: nbHits.value,
    search_category: 'quicksearch',
  });
};

const handleEnter = () => {
  if (activeHit.value > -1 && activeHit.value < displayItems.value.length) {
    const item = displayItems.value[activeHit.value];
    trackQuickSearch();
    trackClick({
      eventName: 'Quick search result opened',
      queryID: queryId.value,
      objectID: item.objectID,
      position: activeHit.value + 1,
    });
    window.location.href = item.url;
  } else {
    window.location.href = searchParamLink.value;
  }
};

const handleClick = (
  event: MouseEvent,
  item: (typeof displayItems.value)[0],
  index: number,
) => {
  event.preventDefault();
  trackQuickSearch();
  trackClick({
    eventName: 'Quick search result opened',
    queryID: queryId.value,
    objectID: item.objectID,
    position: index + 1,
  });
  window.location.href = item.url;
};

const navigateResults = (direction: 'up' | 'down' | number) => {
  if (typeof direction === 'number') {
    activeHit.value = direction;
  } else {
    const step = direction === 'down' ? 1 : -1;
    activeHit.value = Math.min(
      Math.max(activeHit.value + step, -1),
      displayItems.value.length, // last one is the "show more" button
    );
  }
};

const displayItems = computed(() => {
  if (hits.value.length)
    return hits.value
      .slice(0, 3)
      .map((h) => ({ ...h, displayType: 'hit' as const }));
  if (lastHits.value.length)
    return lastHits.value.map((h) => ({
      ...h,
      displayType: 'lastHit' as const,
    }));
  return [];
});

const searchParamLink = computed(() => {
  const url = new URL(window.location.origin + '/search');
  if (query.value.trim()) url.searchParams.set('q', query.value.trim());
  return url.toString();
});

let debounceTimeout: ReturnType<typeof setTimeout>;
const handleInput = () => {
  loading.value = true;
  clearTimeout(debounceTimeout);
  debounceTimeout = setTimeout(async () => {
    await search();
    loading.value = false;
  }, 300);
};
</script>
